import LoadIndicator from 'devextreme-react/load-indicator';
import TagBox from 'devextreme-react/tag-box';
import { useEffect, useState, type CSSProperties } from 'react';
import type { CalendarTarget, CalendarTargetSegment } from '../../../../api/timeOff';
import { listCalendarTargets } from '../../../../api/timeOff';

interface CalendarTargetPickerProps {
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  width?: string | number;
  defaultToFirst?: boolean;
  defaultResetKey?: number | string;
  /** When set with defaultToFirst, prefer the first enabled target in this segment. */
  defaultSegment?: CalendarTargetSegment;
}

const hintStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--ops-color-muted, #6b7280)',
  marginTop: 4,
  lineHeight: 1.35
};

const DEFAULT_APPLIED_KEY = '__initial-default__';

export function CalendarTargetPicker({
  value,
  onChange,
  disabled,
  placeholder,
  width,
  defaultToFirst,
  defaultResetKey,
  defaultSegment
}: CalendarTargetPickerProps) {
  const [targets, setTargets] = useState<CalendarTarget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [defaultAppliedKey, setDefaultAppliedKey] = useState<number | string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listCalendarTargets()
      .then((rows) => {
        if (cancelled) return;
        setTargets(rows.filter((t) => t.isEnabled));
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load calendar targets.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const effectiveDefaultKey = defaultResetKey ?? DEFAULT_APPLIED_KEY;
    if (!defaultToFirst || loading || targets.length === 0 || value.length > 0 || defaultAppliedKey === effectiveDefaultKey) return;
    const preferred =
      defaultSegment != null
        ? targets.find((t) => t.segment === defaultSegment) ?? targets[0]
        : targets[0];
    onChange([preferred.id]);
    setDefaultAppliedKey(effectiveDefaultKey);
  }, [defaultAppliedKey, defaultResetKey, defaultSegment, defaultToFirst, loading, onChange, targets, value.length]);

  const statusHint = (() => {
    if (loading) return 'Loading calendars…';
    if (error) return null;
    if (targets.length === 0) {
      return 'No enabled calendars are configured. Ask an administrator to register calendar targets.';
    }
    if (value.length > 0) {
      return `${value.length} calendar${value.length === 1 ? '' : 's'} selected of ${targets.length} available.`;
    }
    return `${targets.length} calendar${targets.length === 1 ? '' : 's'} available — select one or more.`;
  })();

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
        <TagBox
          items={targets}
          valueExpr="id"
          displayExpr={(t: CalendarTarget) =>
            `${t.displayName} (${t.segment === 'Contract' ? 'Contract' : 'Company'})`}
          value={value}
          onValueChange={(ids: unknown) => onChange(Array.isArray(ids) ? (ids as string[]) : [])}
          disabled={disabled || loading}
          placeholder={placeholder ?? (loading ? 'Loading calendars…' : 'Select calendar(s)')}
          showClearButton
          showSelectionControls
          searchEnabled={targets.length > 4}
          applyValueMode="instantly"
          noDataText="No calendars match your filter."
          width={width ?? '100%'}
        />
        {loading ? <LoadIndicator height={24} width={24} visible /> : null}
      </div>
      {statusHint ? <div style={hintStyle}>{statusHint}</div> : null}
      {error ? <div style={{ ...hintStyle, color: 'var(--ops-color-danger, #b91c1c)' }}>{error}</div> : null}
    </div>
  );
}
